import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Subdistrict, Village } from '@/services/locationService';
import { onboardingService } from '../services/onboardingService';
import type { VillageLookupResponse } from '../services/locationApi';

/**
 * Normalized location data source consumed by `LocationField`. All methods
 * return bare arrays so the widget doesn't have to know about the underlying
 * envelope shapes.
 *
 * `getVillageByLgd` lets the widget hydrate its cascade from a prefilled
 * `village_id` (e.g. when `existing_submission` carries a previously-saved
 * `master_location_lookup_id`). Returning `null` is OK — the widget falls
 * back to showing the raw id.
 */
export interface FormLocationSource {
  getDistricts: () => Promise<string[]>;
  getSubdistricts: (district: string) => Promise<Subdistrict[]>;
  getVillages: (district: string, subdistrict: string) => Promise<Village[]>;
  getVillageByLgd?: (lgdCode: string | number) => Promise<VillageLookupResponse | null>;
}

const FormLocationContext = createContext<FormLocationSource | null>(null);

/**
 * Default backoffice source — points at the invite-mounted location
 * endpoint (`/console/user_onboarding/invite/location/...`) using the
 * backoffice JWT. Same shape as the candidate-portal source; differs only
 * in which axios client carries the auth header. The candidate-portal flow
 * still wraps with its own `FormLocationProvider` so the invite token is
 * used in that tree.
 */
const defaultBackofficeSource: FormLocationSource = {
  getDistricts: () => onboardingService.getOnboardingLocationDistricts(),
  getSubdistricts: (district) => onboardingService.getOnboardingLocationSubdistricts(district),
  getVillages: (district, subdistrict) =>
    onboardingService.getOnboardingLocationVillages(district, subdistrict),
  getVillageByLgd: async (lgdCode) => {
    const v = await onboardingService.getVillageByLgd(lgdCode);
    return v && Object.keys(v).length > 0 ? (v as VillageLookupResponse) : null;
  },
};

export const FormLocationProvider = ({
  source,
  children,
}: {
  source: FormLocationSource;
  children: ReactNode;
}) => {
  return <FormLocationContext.Provider value={source}>{children}</FormLocationContext.Provider>;
};

/**
 * Returns the injected location source, falling back to the backoffice
 * default when no provider wraps the tree. Candidate-portal flows wrap the
 * DynamicForm with an invite-token source pointing at
 * `/console/user_onboarding/invite/location/...`.
 */
export const useFormLocation = (): FormLocationSource => {
  const ctx = useContext(FormLocationContext);
  return useMemo(() => ctx ?? defaultBackofficeSource, [ctx]);
};
